/**
 * Bundle Builder - Green Llama Enhanced
 * Feature-rich bundle builder with configurable tiers, duplicates, and subscriptions
 */

class BundleBuilder {
  constructor(containerSelector = '#bundle-builder') {
    this.container = document.querySelector(containerSelector);

    if (!this.container) {
      console.error('Bundle Builder: Container not found');
      return;
    }

    // Configuration from data attributes
    this.config = {
      minItems: parseInt(this.container.dataset.minItems) || 1,
      maxItems: parseInt(this.container.dataset.maxItems) || 6,
      defaultBundleSize: parseInt(this.container.dataset.defaultBundleSize) || parseInt(this.container.dataset.maxItems) || 4,
      discountTiers: JSON.parse(this.container.dataset.discountTiers || '{}'),
      bundleSizes: JSON.parse(this.container.dataset.bundleSizes || '[]'),
      allowDuplicates: this.container.dataset.allowDuplicates === 'true',
      showBundleSizeSelector: this.container.dataset.showBundleSizeSelector === 'true',
      showSubscriptionOption: this.container.dataset.showSubscriptionOption === 'true',
      subscriptionDiscount: parseInt(this.container.dataset.subscriptionDiscount) || 0,
      showCategoryTracker: this.container.dataset.showCategoryTracker === 'true',
      currency: this.container.dataset.currency || 'GBP',
      addMoreText: 'ADD {count} MORE'
    };

    // State - use defaultBundleSize (first enabled bundle size option)
    this.state = {
      selectedItems: [],
      currentBundleSize: this.config.defaultBundleSize,
      purchaseType: 'one-time',
      isLoading: false,
      isAddingToCart: false,
      hasBundleInCart: false
    };

    // Cache DOM elements
    this.cacheElements();

    // Currency formatter
    this.currencyFormatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: this.config.currency
    });

    this.init();
  }

  /**
   * Cache all DOM elements
   */
  cacheElements() {
    this.elements = {
      productCards: this.container.querySelectorAll('.bundle-product-card'),
      sidebar: document.getElementById('bundle-sidebar'),
      countDisplay: document.getElementById('bundle-count'),
      bundleMaxDisplay: document.getElementById('bundle-max'),
      productsCountDisplay: document.getElementById('products-count'),
      maxItemsDisplay: document.getElementById('max-items-display'),
      selectedItemsContainer: document.getElementById('selected-items'),
      itemsGrid: document.getElementById('items-grid'),
      showToggle: document.getElementById('show-toggle'),
      addMoreBtn: document.getElementById('add-more-btn'),
      addMoreText: document.getElementById('add-more-text'),
      discountCelebration: document.getElementById('discount-celebration'),
      celebrationText: document.getElementById('celebration-text'),
      subtotalDisplay: document.getElementById('bundle-subtotal'),
      discountDisplay: document.getElementById('bundle-discount'),
      discountRow: document.getElementById('discount-row'),
      subscriptionDiscountDisplay: document.getElementById('bundle-subscription-discount'),
      subscriptionRow: document.getElementById('subscription-row'),
      totalDisplay: document.getElementById('bundle-total'),
      addToCartBtn: document.getElementById('add-bundle-to-cart'),
      submitBtnText: document.getElementById('submit-btn-text'),
      progressFill: document.getElementById('progress-indicator-fill'),
      currentMarker: document.getElementById('current-marker'),
      progressIndicator: this.container.querySelector('.bundle-progress-indicator'),
      sidebarToggle: document.getElementById('sidebar-toggle'),
      bundleSizeOptions: this.container.querySelectorAll('.bundle-size-selector__option'),
      frequencyOptions: document.querySelectorAll('.bundle-sidebar__frequency-radio'),
      oneTimePrice: document.getElementById('one-time-price'),
      subscriptionPrice: document.getElementById('subscription-price'),
      oneTimeCompare: document.getElementById('one-time-compare'),
      subscriptionCompare: document.getElementById('subscription-compare'),
      categoryTracker: document.getElementById('category-tracker'),
      categoryList: document.getElementById('category-list'),
      discountTiersInfo: document.getElementById('discount-tiers-info'),
      tierItems: document.querySelectorAll('.bundle-sidebar__tier'),
      sidebarHeader: document.querySelector('.bundle-sidebar__header'),
      expandHint: document.getElementById('expand-hint')
    };

    // Track grid expanded state
    this.gridExpanded = false;

    // Track previous discount rate for celebration
    this.previousDiscountRate = 0;
  }

  /**
   * Initialize event listeners and initial state
   */
  init() {
    // Product card interactions
    this.elements.productCards.forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('.bundle-product-card__variant-select') ||
            e.target.closest('.bundle-product-card__qty-btn') ||
            e.target.closest('.bundle-product-card__quick-remove')) {
          return;
        }
        this.handleProductClick(card);
      });

      const variantSelect = card.querySelector('.bundle-product-card__variant-select');
      variantSelect?.addEventListener('change', (e) => {
        e.stopPropagation();
        this.handleVariantChange(card, e);
      });

      if (this.config.allowDuplicates) {
        const minusBtn = card.querySelector('.bundle-product-card__qty-btn--minus');
        const plusBtn = card.querySelector('.bundle-product-card__qty-btn--plus');

        minusBtn?.addEventListener('click', (e) => {
          e.stopPropagation();
          this.handleQuantityChange(card, -1);
        });

        plusBtn?.addEventListener('click', (e) => {
          e.stopPropagation();
          const vs = card.querySelector('.bundle-product-card__variant-select');
          const vrs = JSON.parse(card.dataset.variants);
          const cv = vs ? vrs.find(v => v.id === parseInt(vs.value)) : vrs[0];
          if (cv && !cv.available) {
            this.showToast(`${card.dataset.productTitle} is currently sold out`);
            return;
          }
          this.handleQuantityChange(card, 1);
        });
      }

      const removeBtn = card.querySelector('.bundle-product-card__quick-remove');
      removeBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removeProductFromBundle(card.dataset.productId);
      });
    });

    // Bundle size selector
    this.elements.bundleSizeOptions.forEach(option => {
      option.addEventListener('click', () => this.handleBundleSizeChange(option));
    });

    // Purchase frequency selector
    this.elements.frequencyOptions.forEach(option => {
      option.addEventListener('change', (e) => this.handleFrequencyChange(e));
    });

    // Add to cart handler
    this.elements.addToCartBtn?.addEventListener('click', () => this.handleAddToCart());

    // Mobile sidebar toggle - make entire header area tappable
    this.elements.sidebarToggle?.addEventListener('click', () => this.toggleSidebar());
    this.elements.sidebarHeader?.addEventListener('click', () => this.toggleSidebar());
    this.elements.expandHint?.addEventListener('click', (e) => {
      e.stopPropagation(); // Prevent double trigger from header click
      this.toggleSidebar();
    });

    // Show toggle for items grid
    this.elements.showToggle?.addEventListener('click', () => this.toggleGridExpand());

    // Add more button - scrolls to product grid
    this.elements.addMoreBtn?.addEventListener('click', () => this.scrollToProducts());

    // Initial render
    this.render();

    // Check if a bundle already exists in the cart
    this.checkCartForExistingBundle();

    // Process prefill from URL (must run after render so DOM is ready)
    this.parsePrefill();

    console.log('Bundle Builder Enhanced initialized', this.config);
  }

  /**
   * Check if a bundle (items with _bundle_id) already exists in the cart.
   * If so, lock the builder to prevent adding a second bundle.
   */
  checkCartForExistingBundle() {
    fetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(res => res.json())
      .then(cart => {
        const hasBundle = cart.items.some(item =>
          item.properties && item.properties._bundle_id
        );
        this.state.hasBundleInCart = hasBundle;
        this.render();
      })
      .catch(() => {
        // Fail silently — allow normal builder behaviour
      });
  }

  /**
   * Handle product card click
   */
  handleProductClick(card) {
    // Block adding unavailable variants
    const variantSelect = card.querySelector('.bundle-product-card__variant-select');
    const variants = JSON.parse(card.dataset.variants);
    const currentVariant = variantSelect
      ? variants.find(v => v.id === parseInt(variantSelect.value))
      : variants[0];

    if (currentVariant && !currentVariant.available) {
      this.showToast(`${card.dataset.productTitle} is currently sold out`);
      this.showShakeAnimation(card);
      return;
    }

    const productId = card.dataset.productId;
    const existingItemIndex = this.state.selectedItems.findIndex(
      item => item.productId === productId
    );

    const totalQuantity = this.getTotalQuantity();

    if (existingItemIndex > -1 && !this.config.allowDuplicates) {
      this.removeItem(existingItemIndex);
    } else if (totalQuantity < this.state.currentBundleSize) {
      if (this.config.allowDuplicates && existingItemIndex > -1) {
        this.state.selectedItems[existingItemIndex].quantity += 1;
        this.render();
        this.showToast(`Added another ${this.state.selectedItems[existingItemIndex].title}`);
      } else {
        this.addItem(card);
      }
    } else {
      this.showShakeAnimation(card);
      this.showToast(`Bundle is full (${this.state.currentBundleSize} items max)`);
    }
  }

  /**
   * Add item to bundle
   */
  addItem(card) {
    const variantSelect = card.querySelector('.bundle-product-card__variant-select');
    const variants = JSON.parse(card.dataset.variants);
    const selectedVariant = variantSelect
      ? variants.find(v => v.id === parseInt(variantSelect.value))
      : variants[0];

    const item = {
      id: this.generateId(),
      productId: card.dataset.productId,
      productHandle: card.dataset.productHandle,
      variantId: selectedVariant.id.toString(),
      title: card.dataset.productTitle,
      variantTitle: selectedVariant.title !== 'Default Title' ? selectedVariant.title : null,
      price: selectedVariant.price,
      image: card.dataset.productImage,
      category: card.dataset.productCategory || null,
      available: selectedVariant.available,
      quantity: 1
    };

    this.state.selectedItems.push(item);
    this.render();

    if (navigator.vibrate) {
      navigator.vibrate(10);
    }

    this.announce(`${item.title} added to bundle`);
  }

  /**
   * Remove item from bundle by index
   */
  removeItem(index) {
    const removed = this.state.selectedItems.splice(index, 1)[0];
    this.render();
    this.announce(`${removed.title} removed from bundle`);
  }

  /**
   * Remove all instances of a product from bundle
   */
  removeProductFromBundle(productId) {
    const index = this.state.selectedItems.findIndex(item => item.productId === productId);
    if (index > -1) {
      this.removeItem(index);
    }
  }

  /**
   * Handle quantity change for a product
   */
  handleQuantityChange(card, delta) {
    const productId = card.dataset.productId;
    const itemIndex = this.state.selectedItems.findIndex(item => item.productId === productId);

    if (itemIndex === -1) return;

    const item = this.state.selectedItems[itemIndex];
    const newQuantity = item.quantity + delta;
    const totalOtherItems = this.getTotalQuantity() - item.quantity;

    if (newQuantity <= 0) {
      this.removeItem(itemIndex);
    } else if (totalOtherItems + newQuantity <= this.state.currentBundleSize) {
      item.quantity = newQuantity;
      this.render();
    } else {
      this.showToast(`Can't add more - bundle is full`);
    }
  }

  /**
   * Handle variant selection change
   */
  handleVariantChange(card, event) {
    const productId = card.dataset.productId;
    const existingIndex = this.state.selectedItems.findIndex(
      item => item.productId === productId
    );

    if (existingIndex > -1) {
      const variants = JSON.parse(card.dataset.variants);
      const newVariant = variants.find(v => v.id === parseInt(event.target.value));

      if (newVariant) {
        this.state.selectedItems[existingIndex].variantId = newVariant.id.toString();
        this.state.selectedItems[existingIndex].variantTitle =
          newVariant.title !== 'Default Title' ? newVariant.title : null;
        this.state.selectedItems[existingIndex].price = newVariant.price;
        this.state.selectedItems[existingIndex].available = newVariant.available;
        this.render();
      }
    }

    const priceDisplay = card.querySelector('.bundle-product-card__price-current');
    const selectedOption = event.target.selectedOptions[0];
    if (priceDisplay && selectedOption) {
      priceDisplay.textContent = this.formatMoney(parseInt(selectedOption.dataset.price));
    }

    // Update button disabled state based on new variant availability
    const variants = JSON.parse(card.dataset.variants);
    const newVariant = variants.find(v => v.id === parseInt(event.target.value));
    const btn = card.querySelector('.bundle-product-card__add-btn');
    if (btn && newVariant) {
      btn.disabled = !newVariant.available;
      card.classList.toggle('is-unavailable', !newVariant.available);
    }
  }

  /**
   * Handle bundle size change
   */
  handleBundleSizeChange(option) {
    const newSize = parseInt(option.dataset.size);

    this.elements.bundleSizeOptions.forEach(opt => {
      opt.classList.remove('is-active');
      opt.setAttribute('aria-pressed', 'false');
    });
    option.classList.add('is-active');
    option.setAttribute('aria-pressed', 'true');

    this.state.currentBundleSize = newSize;

    while (this.getTotalQuantity() > newSize) {
      const lastItem = this.state.selectedItems[this.state.selectedItems.length - 1];
      if (lastItem.quantity > 1) {
        lastItem.quantity -= 1;
      } else {
        this.state.selectedItems.pop();
      }
    }

    // Update max display in progress bar
    if (this.elements.maxItemsDisplay) {
      this.elements.maxItemsDisplay.textContent = newSize;
    }

    // Update bundle max display in sidebar header
    if (this.elements.bundleMaxDisplay) {
      this.elements.bundleMaxDisplay.textContent = newSize;
    }

    // Update progress indicator markers
    this.updateProgressMarkers(newSize);

    this.render();
    this.announce(`Bundle size changed to ${newSize} items`);
  }

  /**
   * Update progress indicator markers for new bundle size
   */
  updateProgressMarkers(newSize) {
    const markersContainer = this.container.querySelector('.bundle-progress-indicator__markers');
    if (!markersContainer) return;

    // Clear existing markers
    markersContainer.innerHTML = '';

    // Create new markers for the current bundle size
    for (let i = 1; i <= newSize; i++) {
      const marker = document.createElement('span');
      marker.className = 'bundle-progress-indicator__marker';
      marker.dataset.position = i;

      // Add discount info if there's a discount at this tier
      const discount = this.config.discountTiers[i.toString()] || 0;
      if (discount > 0) {
        marker.classList.add('has-discount');
        marker.dataset.discount = discount;
      }

      markersContainer.appendChild(marker);
    }
  }

  /**
   * Handle frequency change (one-time vs subscription)
   */
  handleFrequencyChange(event) {
    this.state.purchaseType = event.target.value;

    document.querySelectorAll('.bundle-sidebar__frequency-option').forEach(opt => {
      opt.classList.remove('is-active');
    });
    event.target.closest('.bundle-sidebar__frequency-option').classList.add('is-active');

    this.render();
  }

  /**
   * Get total quantity across all items
   */
  getTotalQuantity() {
    return this.state.selectedItems.reduce((sum, item) => sum + item.quantity, 0);
  }

  /**
   * Calculate totals and discount
   */
  calculateTotals() {
    const itemCount = this.getTotalQuantity();
    const subtotal = this.state.selectedItems.reduce(
      (sum, item) => sum + (item.price * item.quantity), 0
    );

    const discountRate = (this.config.discountTiers[itemCount.toString()] || 0) / 100;
    const bundleDiscountAmount = Math.round(subtotal * discountRate);

    // Calculate one-time total (with bundle discount only)
    const oneTimeTotal = subtotal - bundleDiscountAmount;

    // Always calculate subscription price for display (bundle discount + subscription discount)
    const subscriptionDiscountRate = this.config.subscriptionDiscount / 100;
    const subscriptionDiscountAmount = Math.round(oneTimeTotal * subscriptionDiscountRate);
    const subscriptionTotal = oneTimeTotal - subscriptionDiscountAmount;

    // Determine actual total based on current selection
    const isSubscription = this.state.purchaseType === 'subscription';
    const actualSubscriptionDiscount = isSubscription ? subscriptionDiscountAmount : 0;
    const totalDiscount = bundleDiscountAmount + actualSubscriptionDiscount;
    const total = isSubscription ? subscriptionTotal : oneTimeTotal;

    const itemsRemaining = this.state.currentBundleSize - itemCount;

    return {
      itemCount,
      subtotal,
      discountRate,
      bundleDiscountAmount,
      subscriptionDiscountAmount,
      totalDiscount,
      total,
      oneTimeTotal,
      subscriptionTotal,
      itemsRemaining,
      isComplete: itemCount >= this.state.currentBundleSize,
      isValid: itemCount >= this.config.minItems,
      canAddMore: itemCount < this.state.currentBundleSize
    };
  }

  /**
   * Render the UI
   */
  render() {
    const totals = this.calculateTotals();

    // Check if discount tier has increased (celebration!)
    if (totals.discountRate > this.previousDiscountRate && totals.itemCount > 0) {
      this.showDiscountCelebration(Math.round(totals.discountRate * 100));
    }
    this.previousDiscountRate = totals.discountRate;

    // Update count displays
    if (this.elements.countDisplay) {
      this.elements.countDisplay.textContent = totals.itemCount;
    }
    if (this.elements.productsCountDisplay) {
      this.elements.productsCountDisplay.textContent = totals.itemCount;
    }

    // Update progress indicator
    this.updateProgressIndicator(totals);

    // Update product card states
    this.updateProductCards();

    // Update visual items grid
    this.renderItemsGrid(totals);

    // Update selected items list
    this.renderSelectedItems();

    // Update price displays
    this.updatePriceDisplays(totals);

    // Update frequency option prices
    if (this.elements.oneTimePrice) {
      this.elements.oneTimePrice.textContent = this.formatMoney(totals.oneTimeTotal);
    }
    // Show original price as compare for one-time if there's a bundle discount
    if (this.elements.oneTimeCompare) {
      if (totals.bundleDiscountAmount > 0) {
        this.elements.oneTimeCompare.textContent = this.formatMoney(totals.subtotal);
        this.elements.oneTimeCompare.style.display = '';
      } else {
        this.elements.oneTimeCompare.style.display = 'none';
      }
    }
    if (this.elements.subscriptionPrice) {
      this.elements.subscriptionPrice.textContent = this.formatMoney(totals.subscriptionTotal);
    }
    // Show one-time price as compare for subscription
    if (this.elements.subscriptionCompare && totals.oneTimeTotal > 0) {
      this.elements.subscriptionCompare.textContent = this.formatMoney(totals.oneTimeTotal);
    }

    // Update tier indicators
    this.updateTierIndicators(totals.itemCount);

    // Update add to cart button
    if (this.elements.addToCartBtn) {
      if (this.state.hasBundleInCart) {
        this.elements.addToCartBtn.disabled = true;
        this.elements.addToCartBtn.classList.add('bundle-sidebar__cta--locked');
      } else {
        this.elements.addToCartBtn.disabled = !totals.isValid || this.state.isAddingToCart;
        this.elements.addToCartBtn.classList.remove('bundle-sidebar__cta--locked');
      }
    }

    if (this.elements.submitBtnText) {
      if (this.state.hasBundleInCart) {
        this.elements.submitBtnText.textContent = 'Your Llama already has this bundle!';
      } else if (totals.itemCount > 0) {
        this.elements.submitBtnText.textContent = `Add ${totals.itemCount} Items to Cart`;
      } else {
        this.elements.submitBtnText.textContent = 'Add Bundle to Cart';
      }
    }

    // Show/hide bundle-in-cart lock message
    this.renderLockMessage();

    // Update category tracker
    this.updateCategoryTracker();
  }

  /**
   * Inject or update the lock message below the ATC button when a bundle is already in the cart.
   */
  renderLockMessage() {
    const actions = this.elements.addToCartBtn?.parentElement;
    if (!actions) return;

    let msg = actions.querySelector('.bundle-sidebar__lock-msg');

    if (this.state.hasBundleInCart) {
      if (!msg) {
        msg = document.createElement('div');
        msg.className = 'bundle-sidebar__lock-msg';
        msg.textContent = 'Only one bundle can be active at a time. To start a new one, remove the current bundle from your cart first.';
        actions.appendChild(msg);
      }
    } else if (msg) {
      msg.remove();
    }
  }

  /**
   * Update progress indicator with current marker at fill point
   */
  updateProgressIndicator(totals) {
    const progressPercent = (totals.itemCount / this.state.currentBundleSize) * 100;

    if (this.elements.progressFill) {
      this.elements.progressFill.style.width = `${progressPercent}%`;
    }

    // Show/hide current marker based on count
    if (this.elements.currentMarker) {
      if (totals.itemCount > 0) {
        this.elements.currentMarker.style.display = 'flex';
      } else {
        this.elements.currentMarker.style.display = 'none';
      }
    }

    // Update progress markers
    this.container.querySelectorAll('.bundle-progress-indicator__marker').forEach(marker => {
      const position = parseInt(marker.dataset.position);
      marker.classList.toggle('is-filled', position <= totals.itemCount);
    });

    // Toggle complete state on progress indicator
    if (this.elements.progressIndicator) {
      this.elements.progressIndicator.classList.toggle('is-complete', totals.isComplete);
    }

    // Show/hide max display based on completion
    if (this.elements.maxItemsDisplay) {
      this.elements.maxItemsDisplay.style.display = totals.isComplete ? 'none' : '';
    }
  }

  /**
   * Update tier indicators in sidebar
   */
  updateTierIndicators(itemCount) {
    this.elements.tierItems?.forEach(tier => {
      const tierNum = parseInt(tier.dataset.tier);
      tier.classList.toggle('is-active', itemCount >= tierNum);
    });
  }

  /**
   * Show discount celebration banner
   */
  showDiscountCelebration(discountPercent) {
    if (!this.elements.discountCelebration) return;

    // Update celebration text
    if (this.elements.celebrationText) {
      this.elements.celebrationText.textContent = `${discountPercent}% Discount Unlocked!`;
    }

    // Show the celebration banner
    this.elements.discountCelebration.style.display = 'flex';

    // Re-trigger animation
    this.elements.discountCelebration.style.animation = 'none';
    this.elements.discountCelebration.offsetHeight; // Trigger reflow
    this.elements.discountCelebration.style.animation = '';

    // Haptic feedback if available
    if (navigator.vibrate) {
      navigator.vibrate([50, 50, 100]);
    }

    // Hide after 3 seconds
    clearTimeout(this.celebrationTimeout);
    this.celebrationTimeout = setTimeout(() => {
      if (this.elements.discountCelebration) {
        this.elements.discountCelebration.style.display = 'none';
      }
    }, 3000);

    // Announce for screen readers
    this.announce(`${discountPercent}% discount unlocked!`);
  }

  /**
   * Update product card states
   */
  updateProductCards() {
    this.elements.productCards.forEach(card => {
      const productId = card.dataset.productId;
      const item = this.state.selectedItems.find(item => item.productId === productId);
      const isSelected = !!item;

      card.classList.toggle('is-selected', isSelected);

      const btn = card.querySelector('.bundle-product-card__add-btn');
      if (btn) {
        btn.setAttribute('aria-pressed', isSelected.toString());
      }

      const qtyControls = card.querySelector('.bundle-product-card__quantity-controls');
      if (qtyControls) {
        qtyControls.style.display = isSelected && this.config.allowDuplicates ? 'flex' : 'none';

        const qtyValue = card.querySelector('.bundle-product-card__qty-value');
        if (qtyValue && item) {
          qtyValue.textContent = item.quantity;
        }
      }

      const qtyBadge = card.querySelector('.bundle-product-card__quantity-badge');
      if (qtyBadge && this.config.allowDuplicates) {
        if (isSelected && item.quantity > 1) {
          qtyBadge.style.display = 'flex';
          const countEl = qtyBadge.querySelector('.bundle-product-card__quantity-badge-count');
          if (countEl) countEl.textContent = item.quantity;
        } else {
          qtyBadge.style.display = 'none';
        }
      }

      const removeBtn = card.querySelector('.bundle-product-card__quick-remove');
      if (removeBtn) {
        removeBtn.style.display = isSelected ? 'flex' : 'none';
      }

      // Sync button disabled state with current variant availability
      const variantSelect = card.querySelector('.bundle-product-card__variant-select');
      const variants = JSON.parse(card.dataset.variants);
      const currentVariant = variantSelect
        ? variants.find(v => v.id === parseInt(variantSelect.value))
        : variants[0];
      if (btn && currentVariant) {
        btn.disabled = !currentVariant.available;
        card.classList.toggle('is-unavailable', !currentVariant.available);
      }
    });
  }

  /**
   * Render selected items list
   */
  renderSelectedItems() {
    if (!this.elements.selectedItemsContainer) return;

    if (this.state.selectedItems.length === 0) {
      this.elements.selectedItemsContainer.innerHTML = '';
      return;
    }

    const itemsHtml = this.state.selectedItems.map((item, index) => `
      <div class="bundle-sidebar__item" data-item-id="${item.id}">
        <div class="bundle-sidebar__item-image-wrapper">
          <img src="${item.image}"
               alt="${this.escapeHtml(item.title)}"
               class="bundle-sidebar__item-image"
               width="44"
               height="44"
               loading="lazy">
          ${item.quantity > 1 ? `<span class="bundle-sidebar__item-qty-badge">${item.quantity}</span>` : ''}
        </div>
        <div class="bundle-sidebar__item-info">
          <span class="bundle-sidebar__item-title">${this.escapeHtml(item.title)}</span>
          ${item.variantTitle ? `<span class="bundle-sidebar__item-variant">${this.escapeHtml(item.variantTitle)}</span>` : ''}
        </div>
        <span class="bundle-sidebar__item-price">${this.formatMoney(item.price * item.quantity)}</span>
        <button type="button"
                class="bundle-sidebar__item-remove"
                aria-label="Remove ${this.escapeHtml(item.title)}"
                data-index="${index}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
        </button>
      </div>
    `).join('');

    this.elements.selectedItemsContainer.innerHTML = itemsHtml;

    this.elements.selectedItemsContainer.querySelectorAll('.bundle-sidebar__item-remove')
      .forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.removeItem(parseInt(btn.dataset.index));
        });
      });
  }

  /**
   * Render visual items grid with thumbnails and empty slots
   */
  renderItemsGrid(totals) {
    if (!this.elements.itemsGrid) return;

    const slots = this.elements.itemsGrid.querySelectorAll('.bundle-sidebar__slot');
    const maxItems = this.state.currentBundleSize;

    // Build list of items expanded by quantity
    const expandedItems = [];
    this.state.selectedItems.forEach((item, itemIndex) => {
      for (let q = 0; q < item.quantity; q++) {
        expandedItems.push({ ...item, itemIndex, qtyIndex: q });
      }
    });

    slots.forEach((slot, index) => {
      const slotNum = index + 1;

      // Hide slots beyond current bundle size
      if (slotNum > maxItems) {
        slot.style.display = 'none';
        return;
      }
      slot.style.display = '';

      // Check if we have an item for this slot
      if (index < expandedItems.length) {
        const item = expandedItems[index];
        slot.classList.remove('bundle-sidebar__slot--empty');
        slot.classList.add('bundle-sidebar__slot--filled');

        slot.innerHTML = `
          <div class="bundle-sidebar__slot-inner">
            <img src="${item.image}" alt="${this.escapeHtml(item.title)}" class="bundle-sidebar__slot-image">
            <button type="button" class="bundle-sidebar__slot-remove" data-item-index="${item.itemIndex}" aria-label="Remove ${this.escapeHtml(item.title)}">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        `;

        // Add remove event listener
        const removeBtn = slot.querySelector('.bundle-sidebar__slot-remove');
        removeBtn?.addEventListener('click', (e) => {
          e.stopPropagation();
          const itemIndex = parseInt(removeBtn.dataset.itemIndex);
          const item = this.state.selectedItems[itemIndex];
          if (item && item.quantity > 1) {
            item.quantity -= 1;
            this.render();
          } else {
            this.removeItem(itemIndex);
          }
        });
      } else {
        // Empty slot
        slot.classList.add('bundle-sidebar__slot--empty');
        slot.classList.remove('bundle-sidebar__slot--filled');
        slot.innerHTML = `
          <div class="bundle-sidebar__slot-inner">
            <span class="bundle-sidebar__slot-plus">+</span>
          </div>
        `;

        // Click on empty slot scrolls to products
        slot.addEventListener('click', () => this.scrollToProducts(), { once: true });
      }
    });

    // Update Show All toggle visibility
    if (this.elements.showToggle) {
      // Show toggle only if we have more than 4 items
      this.elements.showToggle.style.display = maxItems > 4 ? '' : 'none';
      const toggleText = this.elements.showToggle.querySelector('.bundle-sidebar__show-toggle-text');
      const toggleIcon = this.elements.showToggle.querySelector('.bundle-sidebar__show-toggle-icon');
      if (toggleText) {
        toggleText.textContent = this.gridExpanded ? 'Show Less' : 'Show All';
      }
      if (toggleIcon) {
        toggleIcon.style.transform = this.gridExpanded ? 'rotate(180deg)' : '';
      }
    }

    // Update Add More button
    if (this.elements.addMoreBtn && this.elements.addMoreText) {
      if (totals.itemsRemaining > 0 && totals.itemCount > 0) {
        this.elements.addMoreBtn.style.display = '';
        const addMoreText = this.config.addMoreText.replace('{count}', totals.itemsRemaining);
        this.elements.addMoreText.textContent = addMoreText;
      } else {
        this.elements.addMoreBtn.style.display = 'none';
      }
    }

    // Manage grid collapsed/expanded state
    if (this.elements.itemsGrid) {
      this.elements.itemsGrid.classList.toggle('is-expanded', this.gridExpanded);
    }
  }

  /**
   * Toggle grid expand/collapse
   */
  toggleGridExpand() {
    this.gridExpanded = !this.gridExpanded;
    this.render();
  }

  /**
   * Scroll to products section
   */
  scrollToProducts() {
    const productsSection = this.container.querySelector('.bundle-products');
    if (productsSection) {
      productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * Update price displays
   */
  updatePriceDisplays(totals) {
    if (this.elements.subtotalDisplay) {
      this.elements.subtotalDisplay.textContent = this.formatMoney(totals.subtotal);
    }

    if (this.elements.discountDisplay) {
      this.elements.discountDisplay.textContent = `-${this.formatMoney(totals.bundleDiscountAmount)}`;
    }

    if (this.elements.discountRow) {
      this.elements.discountRow.style.display = totals.bundleDiscountAmount > 0 ? 'flex' : 'none';
    }

    if (this.elements.subscriptionDiscountDisplay) {
      this.elements.subscriptionDiscountDisplay.textContent =
        `-${this.formatMoney(totals.subscriptionDiscountAmount)}`;
    }

    if (this.elements.subscriptionRow) {
      this.elements.subscriptionRow.style.display = totals.subscriptionDiscountAmount > 0 ? 'flex' : 'none';
    }

    if (this.elements.totalDisplay) {
      this.elements.totalDisplay.textContent = this.formatMoney(totals.total);
    }
  }

  /**
   * Update category tracker
   */
  updateCategoryTracker() {
    if (!this.config.showCategoryTracker || !this.elements.categoryTracker) return;

    const categories = {};
    this.state.selectedItems.forEach(item => {
      if (item.category) {
        categories[item.category] = (categories[item.category] || 0) + item.quantity;
      }
    });

    if (Object.keys(categories).length === 0) {
      this.elements.categoryTracker.style.display = 'none';
      return;
    }

    this.elements.categoryTracker.style.display = 'block';

    const categoriesHtml = Object.entries(categories).map(([cat, count]) => `
      <div class="bundle-category-tracker__category">
        <span class="bundle-category-tracker__category-icon">
          <span class="bundle-category-tracker__category-count">${count}</span>
        </span>
        <span class="bundle-category-tracker__category-name">${this.escapeHtml(cat)}</span>
      </div>
    `).join('');

    if (this.elements.categoryList) {
      this.elements.categoryList.innerHTML = categoriesHtml;
    }
  }

  /**
   * Handle add to cart
   */
  async handleAddToCart() {
    if (this.state.selectedItems.length === 0 || this.state.isAddingToCart) return;

    // Block if a bundle is already in the cart
    if (this.state.hasBundleInCart) {
      this.showToast('You already have a bundle in your cart. Remove it first to add a new one.');
      return;
    }

    // Check for unavailable items before submitting
    const unavailable = this.state.selectedItems.filter(item => item.available === false);
    if (unavailable.length > 0) {
      const names = [...new Set(unavailable.map(i => i.title))].join(', ');
      this.showToast(`Please remove sold-out items before checkout: ${names}`);
      return;
    }

    this.state.isAddingToCart = true;
    this.elements.addToCartBtn?.classList.add('is-loading');
    this.render();

    const bundleId = this.generateBundleId();
    const totals = this.calculateTotals();

    // Consolidate duplicate variants so the same product appears once with higher quantity
    const consolidatedMap = new Map();
    this.state.selectedItems.forEach(item => {
      const key = item.variantId.toString();
      if (consolidatedMap.has(key)) {
        consolidatedMap.get(key).quantity += item.quantity;
      } else {
        consolidatedMap.set(key, { ...item, quantity: item.quantity });
      }
    });

    const items = [];
    let position = 1;

    consolidatedMap.forEach(item => {
      items.push({
        id: parseInt(item.variantId),
        quantity: item.quantity,
        properties: {
          '_bundle_id': bundleId,
          '_bundle_position': position.toString(),
          '_bundle_total_items': totals.itemCount.toString(),
          '_bundle_discount_tier': Math.round(totals.discountRate * 100).toString(),
          '_bundle_original_price': item.price.toString(),
          '_bundle_discounted_price': Math.round(item.price * (1 - totals.discountRate)).toString(),
          '_bundle_purchase_type': this.state.purchaseType
        }
      });
      position++;
    });

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const shopifyMessage = errorData?.description || errorData?.message || '';
        throw new Error(shopifyMessage || 'Failed to add to cart');
      }

      const result = await response.json();
      console.log('Bundle added to cart:', result);

      this.showToast('Bundle added to cart!');

      // If items were prefilled from cart, show replace/keep modal
      // instead of redirecting immediately
      if (this.state.prefillSourceVariants && this.state.prefillSourceVariants.length > 0) {
        this.state.isAddingToCart = false;
        this.elements.addToCartBtn?.classList.remove('is-loading');
        this.showPrefillReplaceModal();
        return;
      }

      window.location.href = '/cart';

    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      this.showToast(error.message || 'Something went wrong. Please try again.');
    } finally {
      this.state.isAddingToCart = false;
      this.elements.addToCartBtn?.classList.remove('is-loading');
      this.render();
    }
  }

  /**
   * Show replace/keep modal after adding a prefilled bundle to cart.
   * The user's original (non-bundled) items are still in the cart;
   * this modal lets them choose to replace or keep both.
   */
  showPrefillReplaceModal() {
    // Remove any existing modal
    const existing = document.querySelector('.bundle-replace-modal');
    if (existing) existing.remove();

    const totalPrefilled = this.state.prefillSourceVariants.reduce(
      (sum, v) => sum + v.quantity, 0
    );
    const itemWord = totalPrefilled === 1 ? 'item' : 'items';

    const overlay = document.createElement('div');
    overlay.className = 'bundle-replace-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'bundle-replace-title');
    overlay.innerHTML = `
      <div class="bundle-replace-modal__backdrop"></div>
      <div class="bundle-replace-modal__content">
        <h3 id="bundle-replace-title" class="bundle-replace-modal__title">
          Bundle added to cart
        </h3>
        <p class="bundle-replace-modal__desc">
          You had ${totalPrefilled} ${itemWord} in your cart that ${totalPrefilled === 1 ? 'is' : 'are'} now
          included in your bundle (with discount). Would you like to remove
          the original ${itemWord} to avoid duplicates?
        </p>
        <div class="bundle-replace-modal__actions">
          <button type="button" class="bundle-replace-modal__btn bundle-replace-modal__btn--primary"
                  data-action="replace">
            Replace in cart
          </button>
          <button type="button" class="bundle-replace-modal__btn bundle-replace-modal__btn--secondary"
                  data-action="keep">
            Keep both
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    // Focus the primary button
    const primaryBtn = overlay.querySelector('[data-action="replace"]');
    primaryBtn?.focus();

    // Handle button clicks
    overlay.addEventListener('click', async (e) => {
      const action = e.target.closest('[data-action]')?.dataset.action;
      const isBackdrop = e.target.classList.contains('bundle-replace-modal__backdrop');

      if (action === 'replace') {
        primaryBtn.textContent = 'Removing...';
        primaryBtn.disabled = true;
        await this.removePrefillOriginals();
        overlay.remove();
        window.location.href = '/cart';
      } else if (action === 'keep' || isBackdrop) {
        overlay.remove();
        window.location.href = '/cart';
      }
    });

    // ESC key closes (same as "Keep both")
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        document.removeEventListener('keydown', handleEsc);
        overlay.remove();
        window.location.href = '/cart';
      }
    };
    document.addEventListener('keydown', handleEsc);

    this.announce(
      'Bundle added to cart. Choose to replace or keep the original items.'
    );
  }

  /**
   * Remove the original (non-bundled) cart items that were used for prefill.
   * Fetches /cart.js to get line item keys, then sets quantity to 0 for
   * matching items that do NOT have a _bundle_id property.
   */
  async removePrefillOriginals() {
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
      });
      const cart = await res.json();

      // Build a set of prefilled variant IDs for fast lookup
      const prefillVids = new Set(
        this.state.prefillSourceVariants.map(v => v.variantId)
      );

      // Find original (non-bundled) line items that match prefilled variants
      const updates = {};
      cart.items.forEach(item => {
        const vid = item.variant_id.toString();
        const isBundleItem = item.properties && item.properties._bundle_id;
        if (prefillVids.has(vid) && !isBundleItem) {
          updates[item.key] = 0;
        }
      });

      if (Object.keys(updates).length === 0) return;

      const updateRes = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      if (updateRes.ok) {
        const updatedCart = await updateRes.json();
        // Emit cart update event to refresh any listening components
        if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
          MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, {
            cart: updatedCart
          });
        }
      }
    } catch (error) {
      console.error('Error removing prefill originals:', error);
      // Don't block navigation — items can be removed manually from cart
    }
  }

  /**
   * Toggle mobile sidebar
   */
  toggleSidebar() {
    const isExpanded = this.elements.sidebarToggle?.getAttribute('aria-expanded') === 'true';
    const newState = !isExpanded;

    this.elements.sidebarToggle?.setAttribute('aria-expanded', newState.toString());
    this.elements.sidebar?.classList.toggle('is-expanded', newState);

    // Update toggle text
    const toggleText = this.elements.sidebarToggle?.querySelector('.bundle-sidebar__expand-text');
    if (toggleText) {
      toggleText.textContent = newState ? 'Hide' : 'Details';
    }

    // Update expand hint text
    if (this.elements.expandHint) {
      this.elements.expandHint.textContent = newState ? 'Tap to collapse' : 'Tap to expand';
    }

    // Also expand/collapse the items grid on mobile
    if (this.gridExpanded !== newState) {
      this.gridExpanded = newState;
      this.render();
    }
  }

  /**
   * Show shake animation on element
   */
  showShakeAnimation(element) {
    element.style.animation = 'none';
    element.offsetHeight;
    element.style.animation = 'shake 0.4s ease';
  }

  /**
   * Format price in currency
   */
  formatMoney(cents) {
    return this.currencyFormatter.format(cents / 100);
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Parse prefill URL parameter and auto-select matching products.
   * URL format: ?prefill=variantId1,variantId2:qty,...
   * Builds items directly into state (no addItem() calls) to avoid
   * duplicate entries and intermediate renders.
   * Cleans the URL after processing.
   */
  parsePrefill() {
    const params = new URLSearchParams(window.location.search);
    const prefillParam = params.get('prefill');
    if (!prefillParam) return;

    const entries = decodeURIComponent(prefillParam).split(',').map(entry => {
      const parts = entry.trim().split(':');
      return { variantId: parts[0], quantity: parseInt(parts[1]) || 1 };
    }).filter(e => e.variantId);

    if (entries.length === 0) return;

    const totalPrefillItems = entries.reduce((sum, e) => sum + e.quantity, 0);
    this.autoSelectBundleSize(totalPrefillItems);

    // Track which variant IDs came from prefill (needed for replace modal)
    this.state.prefillSourceVariants = [];

    let currentTotal = this.getTotalQuantity();

    entries.forEach(entry => {
      if (currentTotal >= this.state.currentBundleSize) return;

      const card = this.findCardByVariantId(entry.variantId);
      if (!card) return;

      // Set the variant dropdown to the correct variant if multi-variant
      const variantSelect = card.querySelector('.bundle-product-card__variant-select');
      if (variantSelect) {
        variantSelect.value = entry.variantId;
      }

      // Resolve the variant data from the card
      const variants = JSON.parse(card.dataset.variants);
      const selectedVariant = variantSelect
        ? variants.find(v => v.id === parseInt(variantSelect.value))
        : variants[0];

      if (!selectedVariant) return;

      // Check if this variant already exists in selectedItems
      const existingIndex = this.state.selectedItems.findIndex(
        item => item.variantId === selectedVariant.id.toString()
      );

      // Determine how many we can actually add
      const qty = this.config.allowDuplicates
        ? Math.min(entry.quantity, this.state.currentBundleSize - currentTotal)
        : 1;

      if (qty <= 0) return;

      if (existingIndex > -1 && this.config.allowDuplicates) {
        // Increment existing entry's quantity
        this.state.selectedItems[existingIndex].quantity += qty;
      } else if (existingIndex === -1) {
        // Build item object directly (same shape as addItem() produces)
        this.state.selectedItems.push({
          id: this.generateId(),
          productId: card.dataset.productId,
          productHandle: card.dataset.productHandle,
          variantId: selectedVariant.id.toString(),
          title: card.dataset.productTitle,
          variantTitle: selectedVariant.title !== 'Default Title' ? selectedVariant.title : null,
          price: selectedVariant.price,
          image: card.dataset.productImage,
          category: card.dataset.productCategory || null,
          available: selectedVariant.available,
          quantity: qty
        });
      }

      currentTotal += qty;

      // Record this variant as a prefill source
      this.state.prefillSourceVariants.push({
        variantId: selectedVariant.id.toString(),
        quantity: qty
      });
    });

    // Clean URL without triggering a page reload
    params.delete('prefill');
    const cleanUrl = window.location.pathname +
      (params.toString() ? '?' + params.toString() : '') +
      window.location.hash;
    window.history.replaceState({}, '', cleanUrl);

    // Suppress celebration animation for prefilled items
    this.previousDiscountRate = this.calculateTotals().discountRate;

    // Single render after all state changes
    this.render();

    const prefillCount = this.getTotalQuantity();
    if (prefillCount > 0) {
      this.announce(
        prefillCount + ' item' + (prefillCount > 1 ? 's' : '') +
        ' from your cart have been added to the bundle builder'
      );
    }
  }

  /**
   * Find a product card DOM element that contains a given variant ID.
   */
  findCardByVariantId(variantId) {
    for (const card of this.elements.productCards) {
      const variants = JSON.parse(card.dataset.variants);
      const match = variants.find(v => v.id.toString() === variantId.toString());
      if (match) return card;
    }
    return null;
  }

  /**
   * Programmatically select the smallest bundle size >= itemCount.
   * Falls back to the largest available size.
   */
  autoSelectBundleSize(itemCount) {
    if (!this.config.showBundleSizeSelector) return;

    const sizes = Array.from(this.elements.bundleSizeOptions)
      .map(opt => ({ element: opt, size: parseInt(opt.dataset.size) }))
      .sort((a, b) => a.size - b.size);

    if (sizes.length === 0) return;

    const target = sizes.find(s => s.size >= itemCount) || sizes[sizes.length - 1];
    this.handleBundleSizeChange(target.element);
  }

  /**
   * Generate unique ID for item
   */
  generateId() {
    return `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Generate unique bundle ID
   */
  generateBundleId() {
    return `bnd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Show toast message
   */
  showToast(message) {
    const existing = document.querySelector('.bundle-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'bundle-toast';
    toast.textContent = message;

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('is-hiding');
      setTimeout(() => toast.remove(), 300);
    }, 2500);
  }

  /**
   * Announce for screen readers
   */
  announce(message) {
    const announcer = document.getElementById('bundle-announcer') || this.createAnnouncer();
    announcer.textContent = message;
  }

  /**
   * Create ARIA live region for announcements
   */
  createAnnouncer() {
    const announcer = document.createElement('div');
    announcer.id = 'bundle-announcer';
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    announcer.className = 'sr-only';
    document.body.appendChild(announcer);
    return announcer;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new BundleBuilder());
} else {
  new BundleBuilder();
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = BundleBuilder;
}
